import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { locationAPI } from '../../services/api';
import MasterDataBulkUploadDialog from '../components/MasterDataBulkUploadDialog';
import useIsMobile from '../../hooks/useIsMobile';

// Each level declares which parent selectors it needs (in order) and the
// api functions that back it — one generic panel component below renders
// all 4 from this config instead of 4 near-duplicate page files.
const LEVELS = {
  phc: {
    title: 'प्राथमिक आरोग्य केंद्र (PHC)',
    singular: 'PHC',
    parentChain: ['district', 'taluka'],
    api: {
      list: locationAPI.getPHCs,
      create: locationAPI.createPHC,
      update: locationAPI.updatePHC,
      delete: locationAPI.deletePHC,
      bulkUpload: locationAPI.bulkUploadPHCs,
    },
    extraFields: [
      { key: 'village', label: 'गाव', required: false },
      { key: 'pincode', label: 'पिनकोड', required: false },
    ],
    bulkColumns: [
      { key: 'name', required: true, example: 'PHC Dabhad' },
      { key: 'district', required: true, example: 'Thane' },
      { key: 'taluka', required: true, example: 'Bhiwandi' },
      { key: 'village', required: false, example: '' },
      { key: 'pincode', required: false, example: '' },
    ],
  },
  subCentre: {
    title: 'उप-केंद्र (Sub-Centre)',
    singular: 'उप-केंद्र',
    parentChain: ['district', 'taluka', 'phc'],
    api: {
      list: locationAPI.getSubCentres,
      create: locationAPI.createSubCentre,
      update: locationAPI.updateSubCentre,
      delete: locationAPI.deleteSubCentre,
      bulkUpload: locationAPI.bulkUploadSubCentres,
    },
    extraFields: [],
    bulkColumns: [
      { key: 'name', required: true, example: 'उप-केंद्र वाशिंद' },
      { key: 'district', required: true, example: 'Thane' },
      { key: 'taluka', required: true, example: 'Shahapur' },
      { key: 'phc', required: true, example: 'PHC Vashind' },
    ],
  },
  village: {
    title: 'गाव (Village)',
    singular: 'गाव',
    parentChain: ['district', 'taluka', 'phc', 'subCentre'],
    api: {
      list: locationAPI.getVillages,
      create: locationAPI.createVillage,
      update: locationAPI.updateVillage,
      delete: locationAPI.deleteVillage,
      bulkUpload: locationAPI.bulkUploadVillages,
    },
    extraFields: [
      { key: 'population', label: 'लोकसंख्या', required: false, type: 'number' },
      { key: 'pincode', label: 'पिनकोड', required: false },
    ],
    bulkColumns: [
      { key: 'name', required: true, example: 'दाभाड' },
      { key: 'district', required: true, example: 'Thane' },
      { key: 'taluka', required: true, example: 'Bhiwandi' },
      { key: 'phc', required: true, example: 'PHC Dabhad' },
      { key: 'sub_centre', required: true, example: 'उप-केंद्र दाभाड' },
      { key: 'population', required: false, example: '' },
      { key: 'pincode', required: false, example: '' },
    ],
  },
  gatpravartak: {
    title: 'नियुक्त गटप्रवर्तक (Gatpravartak)',
    singular: 'गटप्रवर्तक',
    parentChain: ['district', 'taluka', 'phc'],
    api: {
      list: locationAPI.getGatpravartaks,
      create: locationAPI.createGatpravartak,
      update: locationAPI.updateGatpravartak,
      delete: locationAPI.deleteGatpravartak,
      bulkUpload: locationAPI.bulkUploadGatpravartaks,
    },
    extraFields: [],
    bulkColumns: [
      { key: 'name', required: true, example: 'राम पाटील' },
      { key: 'district', required: true, example: 'Thane' },
      { key: 'taluka', required: true, example: 'Bhiwandi' },
      { key: 'phc', required: true, example: 'PHC Dabhad' },
    ],
  },
};

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('admin_user') || 'null');
  } catch {
    return null;
  }
};

function MasterDataPanel({ levelKey }) {
  const isMobile = useIsMobile();
  const config = LEVELS[levelKey];
  const isTalukaAdmin = getCurrentUser()?.role === 'TALUKA_ADMIN';

  const [districts, setDistricts] = useState([]);
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [phcOptions, setPhcOptions] = useState([]);
  const [subCentreOptions, setSubCentreOptions] = useState([]);

  // Filter selections (also drives which parents a new item is created under)
  const [filters, setFilters] = useState({ district: '', taluka: '', phc: '', subCentre: '' });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [form, setForm] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);

  // Load districts once
  useEffect(() => {
    locationAPI.getDistricts().then((res) => setDistricts(res.data)).catch(() => {});
  }, []);

  // Cascade: district -> talukaOptions
  useEffect(() => {
    const d = districts.find((x) => x._id === filters.district);
    setTalukaOptions(d?.talukas || []);
  }, [filters.district, districts]);

  // Cascade: taluka -> phcOptions (needed once phc appears in the parent chain)
  useEffect(() => {
    if (!config.parentChain.includes('phc')) return;
    if (!filters.taluka) { setPhcOptions([]); return; }
    locationAPI.getPHCs({ taluka_id: filters.taluka }).then((res) => setPhcOptions(res.data)).catch(() => {});
  }, [filters.taluka, config.parentChain]);

  // Cascade: phc -> subCentreOptions (needed once subCentre appears in the parent chain)
  useEffect(() => {
    if (!config.parentChain.includes('subCentre')) return;
    if (!filters.phc) { setSubCentreOptions([]); return; }
    locationAPI.getSubCentres({ phc_id: filters.phc }).then((res) => setSubCentreOptions(res.data)).catch(() => {});
  }, [filters.phc, config.parentChain]);

  const fetchItems = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (filters.district) params.district_id = filters.district;
    if (filters.taluka) params.taluka_id = filters.taluka;
    if (filters.phc && config.parentChain.includes('phc')) params.phc_id = filters.phc;
    if (filters.subCentre && config.parentChain.includes('subCentre')) params.sub_centre_id = filters.subCentre;
    params.include_inactive = true;

    config.api.list(params)
      .then((res) => setItems(res.data))
      .catch(() => setError(`${config.title} लोड करताना त्रुटी आली`))
      .finally(() => setLoading(false));
  }, [filters, config]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetFilters = () => setFilters({ district: '', taluka: '', phc: '', subCentre: '' });

  // A TALUKA_ADMIN caller can only ever create master data within their own
  // taluka (backend-enforced in locations.py's _require_own_taluka) —
  // resolve their own district/taluka _id from the districts tree so the
  // create dialog can pre-fill and lock these fields.
  const getTalukaAdminOwnIds = () => {
    const ownTalukaId = getCurrentUser()?.taluka_id;
    if (!ownTalukaId) return { district_id: '', taluka_id: '' };
    for (const d of districts) {
      if ((d.talukas || []).some((t) => t._id === ownTalukaId)) {
        return { district_id: d._id, taluka_id: ownTalukaId };
      }
    }
    return { district_id: '', taluka_id: ownTalukaId };
  };

  // ---- Add/Edit dialog ----
  const openCreate = () => {
    const ownIds = isTalukaAdmin ? getTalukaAdminOwnIds() : null;
    setForm({
      name: '',
      district_id: ownIds ? ownIds.district_id : (filters.district || ''),
      taluka_id: ownIds ? ownIds.taluka_id : (filters.taluka || ''),
      phc_id: filters.phc || '',
      sub_centre_id: filters.subCentre || '',
      is_active: true,
      ...Object.fromEntries(config.extraFields.map((f) => [f.key, ''])),
    });
    setDialog({ open: true, mode: 'create', data: null });
  };

  const openEdit = (item) => {
    setForm({
      name: item.name,
      district_id: item.district_id,
      taluka_id: item.taluka_id,
      phc_id: item.phc_id || '',
      sub_centre_id: item.sub_centre_id || '',
      is_active: item.is_active,
      ...Object.fromEntries(config.extraFields.map((f) => [f.key, item[f.key] ?? ''])),
    });
    setDialog({ open: true, mode: 'edit', data: item });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', data: null });

  const handleSave = async () => {
    setError('');
    try {
      if (dialog.mode === 'create') {
        const payload = { name: form.name, district_id: form.district_id, taluka_id: form.taluka_id, is_active: true };
        if (config.parentChain.includes('phc')) payload.phc_id = form.phc_id;
        if (config.parentChain.includes('subCentre')) payload.sub_centre_id = form.sub_centre_id;
        config.extraFields.forEach((f) => {
          payload[f.key] = f.type === 'number' ? (form[f.key] ? Number(form[f.key]) : null) : (form[f.key] || null);
        });
        await config.api.create(payload);
        setSuccess(`${config.singular} यशस्वीरित्या जोडले`);
      } else {
        const payload = { name: form.name };
        if (config.parentChain.includes('phc')) payload.phc_id = form.phc_id;
        if (config.parentChain.includes('subCentre')) payload.sub_centre_id = form.sub_centre_id;
        config.extraFields.forEach((f) => {
          payload[f.key] = f.type === 'number' ? (form[f.key] ? Number(form[f.key]) : null) : (form[f.key] || null);
        });
        await config.api.update(dialog.data._id, payload);
        setSuccess(`${config.singular} यशस्वीरित्या अद्ययावत केले`);
      }
      closeDialog();
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.detail || 'जतन करताना त्रुटी आली');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await config.api.update(item._id, { is_active: !item.is_active });
      fetchItems();
    } catch {
      setError('स्थिती बदलताना त्रुटी आली');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" डिलीट करायचे?`)) return;
    try {
      await config.api.delete(item._id);
      setSuccess(`${config.singular} डिलीट केले`);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.detail || 'डिलीट करताना त्रुटी आली');
    }
  };

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select fullWidth size="small" label="जिल्हा"
              value={filters.district}
              onChange={(e) => setFilters({ district: e.target.value, taluka: '', phc: '', subCentre: '' })}
            >
              <MenuItem value="">सर्व</MenuItem>
              {districts.map((d) => <MenuItem key={d._id} value={d._id}>{d.name_mr}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select fullWidth size="small" label="तालुका"
              value={filters.taluka}
              disabled={!filters.district}
              onChange={(e) => setFilters((f) => ({ ...f, taluka: e.target.value, phc: '', subCentre: '' }))}
            >
              <MenuItem value="">सर्व</MenuItem>
              {talukaOptions.map((t) => <MenuItem key={t._id} value={t._id}>{t.name_mr}</MenuItem>)}
            </TextField>
          </Grid>
          {config.parentChain.includes('phc') && (
            <Grid item xs={12} sm={3}>
              <TextField
                select fullWidth size="small" label="PHC"
                value={filters.phc}
                disabled={!filters.taluka}
                onChange={(e) => setFilters((f) => ({ ...f, phc: e.target.value, subCentre: '' }))}
              >
                <MenuItem value="">सर्व</MenuItem>
                {phcOptions.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          {config.parentChain.includes('subCentre') && (
            <Grid item xs={12} sm={3}>
              <TextField
                select fullWidth size="small" label="उप-केंद्र"
                value={filters.subCentre}
                disabled={!filters.phc}
                onChange={(e) => setFilters((f) => ({ ...f, subCentre: e.target.value }))}
              >
                <MenuItem value="">सर्व</MenuItem>
                {subCentreOptions.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item>
            <Button size="small" onClick={resetFilters}>फिल्टर साफ करा</Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<UploadIcon />} variant="outlined" onClick={() => setBulkOpen(true)}>
          बल्क अपलोड
        </Button>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          {config.singular} जोडा
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>नाव</TableCell>
                {config.extraFields.map((f) => <TableCell key={f.key}>{f.label}</TableCell>)}
                <TableCell>स्थिती</TableCell>
                <TableCell align="right">क्रिया</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={4 + config.extraFields.length} align="center">कोणतीही नोंद नाही</TableCell></TableRow>
              )}
              {items.map((item) => (
                <TableRow key={item._id}>
                  <TableCell>{item.name}</TableCell>
                  {config.extraFields.map((f) => <TableCell key={f.key}>{item[f.key] ?? '-'}</TableCell>)}
                  <TableCell>
                    <Chip
                      label={item.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                      color={item.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleToggleActive(item)} title="सक्रिय/निष्क्रिय">
                      {item.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dialog.mode === 'create' ? `नवीन ${config.singular}` : `${config.singular} संपादित करा`}
          <IconButton onClick={closeDialog} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="नाव *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>

            {dialog.mode === 'create' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth label="जिल्हा *" value={form.district_id || ''}
                    disabled={isTalukaAdmin}
                    onChange={(e) => setForm({ ...form, district_id: e.target.value, taluka_id: '', phc_id: '', sub_centre_id: '' })}
                  >
                    {districts.map((d) => <MenuItem key={d._id} value={d._id}>{d.name_mr}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth label="तालुका *" value={form.taluka_id || ''}
                    disabled={!form.district_id || isTalukaAdmin}
                    onChange={(e) => setForm({ ...form, taluka_id: e.target.value, phc_id: '', sub_centre_id: '' })}
                  >
                    {(districts.find((d) => d._id === form.district_id)?.talukas || []).map((t) => (
                      <MenuItem key={t._id} value={t._id}>{t.name_mr}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {config.parentChain.includes('phc') && (
                  <Grid item xs={12} sm={6}>
                    <PhcSelect talukaId={form.taluka_id} value={form.phc_id} onChange={(v) => setForm({ ...form, phc_id: v, sub_centre_id: '' })} />
                  </Grid>
                )}
                {config.parentChain.includes('subCentre') && (
                  <Grid item xs={12} sm={6}>
                    <SubCentreSelect phcId={form.phc_id} value={form.sub_centre_id} onChange={(v) => setForm({ ...form, sub_centre_id: v })} />
                  </Grid>
                )}
              </>
            )}

            {config.extraFields.map((f) => (
              <Grid item xs={12} sm={6} key={f.key}>
                <TextField
                  fullWidth label={f.label} type={f.type || 'text'}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>रद्द करा</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>जतन करा</Button>
        </DialogActions>
      </Dialog>

      <MasterDataBulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={fetchItems}
        title={`${config.title} बल्क अपलोड`}
        columns={config.bulkColumns}
        uploadFn={config.api.bulkUpload}
      />
    </Box>
  );
}

// Small helper selects used only inside the create dialog above, where the
// parent (taluka_id / phc_id) can change independently of the page's filter
// bar selections.
function PhcSelect({ talukaId, value, onChange }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!talukaId) { setOptions([]); return; }
    locationAPI.getPHCs({ taluka_id: talukaId }).then((res) => setOptions(res.data)).catch(() => {});
  }, [talukaId]);
  return (
    <TextField select fullWidth label="PHC *" value={value || ''} disabled={!talukaId} onChange={(e) => onChange(e.target.value)}>
      {options.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
    </TextField>
  );
}

function SubCentreSelect({ phcId, value, onChange }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!phcId) { setOptions([]); return; }
    locationAPI.getSubCentres({ phc_id: phcId }).then((res) => setOptions(res.data)).catch(() => {});
  }, [phcId]);
  return (
    <TextField select fullWidth label="उप-केंद्र *" value={value || ''} disabled={!phcId} onChange={(e) => onChange(e.target.value)}>
      {options.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
    </TextField>
  );
}

const TABS = [
  { key: 'phc', label: 'PHC' },
  { key: 'subCentre', label: 'उप-केंद्र' },
  { key: 'village', label: 'गाव' },
  { key: 'gatpravartak', label: 'गटप्रवर्तक' },
];

function MasterDataManagement() {
  const [tab, setTab] = useState(0);
  // TALUKA_ADMIN now gets every tab here including Gatpravartak — same
  // taluka-locked create dialog as PHC/Sub-Centre/Village (see
  // getTalukaAdminOwnIds/isTalukaAdmin inside MasterDataPanel above), backed
  // by the same taluka scoping on the backend (create_gatpravartak /
  // locations.py). SUPERVISOR never reaches this page at all (no route to
  // it), and is blocked outright server-side either way.

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>मास्टर डेटा व्यवस्थापन</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        तालुका → PHC → उप-केंद्र → गाव या श्रेणीबद्ध रचनेतील मास्टर डेटा — इथे जोडलेला/अपलोड केलेला डेटा अ‍ॅपमधील ड्रॉपडाउनमध्ये आपोआप उपलब्ध होतो.
        (तालुका जोडणे/संपादन "जिल्हा व्यवस्थापन" पानावर आहे.)
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        {TABS.map((t) => <Tab key={t.key} label={t.label} />)}
      </Tabs>

      <MasterDataPanel key={TABS[tab].key} levelKey={TABS[tab].key} />
    </Container>
  );
}

export default MasterDataManagement;
